export type ChatInputTagKind = 'quickAction' | 'document';

interface ITagStyle {
  className: string;
  prefix: string;
}

const TAG_STYLES: Record<ChatInputTagKind, ITagStyle> = {
  quickAction: { className: 'chat-input__quick-action-tag', prefix: '/' },
  document: { className: 'chat-input__document-tag', prefix: '@' }
};

const TAG_KINDS = Object.keys(TAG_STYLES) as ChatInputTagKind[];

const MENTION_PREFIX = '@';

const isSpace = (char: string): boolean => !char.trim();

const buildTag = (
  kind: ChatInputTagKind,
  label: string,
  iconClass?: string
): HTMLSpanElement => {
  const { className, prefix } = TAG_STYLES[kind];
  const tag = document.createElement('span');
  tag.className = className;
  tag.contentEditable = 'false';

  if (iconClass) {
    const icon = document.createElement('i');
    icon.className = `${iconClass} ${className}-icon`;
    tag.appendChild(icon);
    tag.appendChild(document.createTextNode(` ${label}`));
    return tag;
  }

  tag.textContent = `${prefix}${label}`;
  return tag;
};

const currentRangeIn = (el: HTMLElement): Range | null => {
  const selection = window.getSelection();
  if (!selection?.rangeCount) {
    return null;
  }
  const range = selection.getRangeAt(0);
  return el.contains(range.startContainer) ? range : null;
};

const textBeforeCursor = (el: HTMLElement, range: Range): string => {
  const upToCursor = range.cloneRange();
  upToCursor.selectNodeContents(el);
  upToCursor.setEnd(range.startContainer, range.startOffset);
  return upToCursor.toString();
};

const placeCursorAfter = (node: Node): void => {
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};

const selectedClass = (kind: ChatInputTagKind): string =>
  `${TAG_STYLES[kind].className}--selected`;

const findTag = (el: HTMLElement, kind: ChatInputTagKind): HTMLElement | null =>
  el.querySelector(`.${TAG_STYLES[kind].className}`);

export function getTextContent(el: HTMLElement): string {
  const clone = el.cloneNode(true) as HTMLElement;
  TAG_KINDS.forEach(kind => {
    clone.querySelectorAll(`.${TAG_STYLES[kind].className}`).forEach(tag => tag.remove());
  });
  return (clone.innerText || clone.textContent || '').replace(/\n$/, '');
}

export function adjustHeight(el: HTMLElement): void {
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}

export function placeCursorAtEnd(el: HTMLElement): void {
  el.focus();
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

export function clearEditable(el: HTMLElement): void {
  el.innerHTML = '';
  adjustHeight(el);
}

export function renderTag(
  el: HTMLElement,
  kind: ChatInputTagKind,
  label: string,
  iconClass?: string
): void {
  el.innerHTML = '';
  el.appendChild(buildTag(kind, label, iconClass));
  el.appendChild(document.createTextNode('\u00A0'));
}

// `null` fora de uma menção: o `@` precisa iniciar palavra (senão e-mail
// vira menção) e o espaço encerra o termo.
export function readMentionQuery(el: HTMLElement): string | null {
  const range = currentRangeIn(el);
  if (!range) {
    return null;
  }

  const before = textBeforeCursor(el, range);
  const at = before.lastIndexOf(MENTION_PREFIX);
  if (at === -1) {
    return null;
  }

  const charBefore = at > 0 ? before[at - 1] : '';
  if (charBefore && !isSpace(charBefore)) {
    return null;
  }

  const query = before.slice(at + 1);
  return [...query].some(isSpace) ? null : query;
}

export function replaceMentionWithTag(
  el: HTMLElement,
  kind: ChatInputTagKind,
  label: string,
  iconClass?: string
): void {
  const range = currentRangeIn(el);
  const node = range?.startContainer;
  if (!range || !node || node.nodeType !== Node.TEXT_NODE) {
    return;
  }

  const text = node.textContent ?? '';
  const at = text.slice(0, range.startOffset).lastIndexOf(MENTION_PREFIX);
  if (at === -1) {
    return;
  }

  const mention = document.createRange();
  mention.setStart(node, at);
  mention.setEnd(node, range.startOffset);
  mention.deleteContents();

  const spacer = document.createTextNode('\u00A0');
  mention.insertNode(spacer);
  mention.insertNode(buildTag(kind, label, iconClass));

  placeCursorAfter(spacer);
}

export function removeTag(el: HTMLElement, kind: ChatInputTagKind): void {
  findTag(el, kind)?.remove();
}

export function updateTagSelection(
  el: HTMLElement,
  kind: ChatInputTagKind,
  selected: boolean
): void {
  const tag = findTag(el, kind);
  if (!tag) {
    return;
  }
  tag.classList.toggle(selectedClass(kind), selected);
}
